import {
    onArticleClick,
    onArticleUnsubscribe
} from 'components/main/saved-articles/events'
import { image } from 'components/main/saved-articles/image'
import {
    DELETE_ICON,
    BOOKMARK_ICON
} from 'components/main/saved-articles/icons'
import {
    SavedArticleResponse,
    SavedArticle
} from 'components/main/saved-articles/interfaces'
import { CustomObjectApiService } from 'components/main/user-custom-objects/api-service'
import { LocalStorageKey } from 'constants/enums'
import { ImageService } from 'services/image/image.service'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { attachEventHandlerWithFemFlagCheck } from 'utils/events'

export class SavedArticlesPageComponent extends ComponentBase {
    private el: HTMLDivElement
    private listContainer: HTMLDivElement | undefined = undefined
    private savedArticles: SavedArticle[]
    private readonly fallbackImageUrl: string
    private readonly articleLimitRegister: string
    private readonly articleLimitSubscriber: string
    private currentPage = 1
    // eslint-disable-next-line no-magic-numbers
    private readonly perPage = 10
    private objectId = ''
    private objectModifiedOn = ''
    private alertContainer?: HTMLDivElement = undefined
    private alertMessage?: HTMLDivElement = undefined
    private alertButton?: HTMLButtonElement = undefined
    private alertTimer?: NodeJS.Timeout = undefined
    // eslint-disable-next-line no-magic-numbers
    private readonly SAVED_CHANGES_DISMISSED_TIMEOUT = 3000

    public constructor(protected widget: HTMLDivElement) {
        super()
        this.el = widget
        this.savedArticles = []
        this.fallbackImageUrl = this.el.dataset.fallbackImage || ''
        this.articleLimitRegister = this.el.dataset.articleLimitRegister || '20'
        this.articleLimitSubscriber =
            this.el.dataset.articleLimitSubscriber || '100'

        this.el.innerHTML = ''
        this.el.appendChild(this.buildMainContainer())
        this.alertContainer = document.getElementsByClassName(
            'saved-articles--alert'
        )[0] as HTMLDivElement
        this.alertMessage = document.getElementsByClassName(
            'saved-articles--alert--inner--text'
        )[0] as HTMLDivElement
        this.alertButton = document.getElementsByClassName(
            'saved-articles--alert--inner--button'
        )[0] as HTMLButtonElement
        this.alertButton.onclick = this.closeMessage.bind(this)
        attachEventHandlerWithFemFlagCheck('pnIdReady', () => {
            if (this.userType == 'anonymous') {
                window.location.href = '/sign-in'
                return
            }
            void this.loadData()
        })
    }

    private get token() {
        return localStorage.getItem(LocalStorageKey.LRTOKENKEY) || ''
    }

    private async loadData() {
        const url = location.origin
        const headers = new Headers()
        if (!this.token) return
        headers.append('X-API-U-Token', this.token)

        const response = await fetch(
            `${url}/api-root/subscription/identity/custom-objects/default/saved-articles/`,
            { headers }
        )
        let data: SavedArticleResponse

        if (response.ok) {
            data = (await response.json()) as SavedArticleResponse
        } else {
            data = {
                data: {
                    object_id: '',
                    object_modified_on: '',
                    saved_articles: {}
                }
            }
        }
        this.savedArticles = Object.values(data.data.saved_articles)
            .sort((article1, article2) => article1.saved_on - article2.saved_on)
            .reverse()
        this.objectId = data.data.object_id
        this.objectModifiedOn = data.data.object_modified_on
        if (this.savedArticles.length === 0) {
            this.renderEmptyState()
        } else {
            this.renderArticleList()
        }
        this.renderBanner()
    }

    private buildMainContainer(): HTMLDivElement {
        const element = document.createElement('div')
        element.classList.add('saved-articles--container')

        this.listContainer = document.createElement('div')

        element.appendChild(this.listContainer)
        return element
    }

    private get savedArticlesView(): SavedArticle[] {
        return this.savedArticles.slice(
            (this.currentPage - 1) * this.perPage,
            this.currentPage * this.perPage
        )
    }

    private renderBanner() {
        let innerHTML = ''

        switch (this.userType) {
            case 'register':
                // language=HTML
                innerHTML = `
                    <h3 class='saved-articles--banner--heading'>Saved Articles</h3>
                    <p>You can save up to ${this.articleLimitRegister} articles. <a href='/subscribe/'>Subscribe</a> and
                        you can save up to 100 articles!</p>
                `
                break

            case 'subscriber':
                // language=HTML
                innerHTML = `
                    <h3 class='saved-articles--banner--heading'>Saved Articles</h3>
                    <p>You can save up to ${this.articleLimitSubscriber} articles. Unread articles will automatically be
                        removed after 90 days.</p>
                `
                break
        }

        const banner = document.getElementsByClassName(
            'saved-articles--banner'
        )[0]
        if (!banner) return

        banner.innerHTML = innerHTML
    }

    private renderArticleList() {
        const fragment = document.createDocumentFragment()
        const articles = this.savedArticlesView.map((article) =>
            this.renderArticle(article)
        )
        for (const article of articles) {
            fragment.appendChild(article)
        }
        if (this.listContainer) {
            this.listContainer.textContent = ''
            this.listContainer.appendChild(fragment)
        }
        new ImageService() // Re-trigger image-service lazyload
        this.renderPagination()
    }

    private fillArticleCardWithData(
        element: HTMLElement,
        article: SavedArticle
    ) {
        // Grab elements from template to fill them with data
        const link = element.getElementsByClassName(
            'article-card__link'
        )[0] as HTMLAnchorElement
        const title = element.getElementsByClassName(
            'article-card__headline'
        )[0] as HTMLHeadingElement
        const titleClamp = title.getElementsByClassName(
            'article-card__headline-clamp'
        )[0] as HTMLSpanElement
        const excerpt = element.getElementsByClassName(
            'article-card__excerpt'
        )[0] as HTMLParagraphElement
        const time = element.getElementsByClassName(
            'article-card__time-clamp'
        )[0] as HTMLSpanElement

        // Fill template elements with data
        link.href = `/${article.origin_url_path}`
        link.onclick = () => {
            onArticleClick(article)
        }

        title.title = article.title
        titleClamp.innerText = article.title
        excerpt.innerText = article.article_excerpt
        time.innerText = article.published_since
    }

    private renderArticle(article: SavedArticle): HTMLElement {
        const element = document.createElement('div')
        element.classList.add('saved-articles--article-block')

        const deleteButton = document.createElement('a')
        deleteButton.classList.add('saved-articles--delete-button')
        deleteButton.onclick = async () => {
            await this.removeArticle(article)
            onArticleUnsubscribe(article)
        }
        deleteButton.innerHTML = DELETE_ICON

        // language=HTML
        element.innerHTML = `
            <article class='article-card article-card--two-column article-card--no-excerpt--sm-down'>
                <div class='article-card__content first'>
                    <div class='article-card__details'>
                        <a class='article-card__link' href=''>
                            <h3 class='article-card__headline text-size--extra-large--sm-up'>
                                <span class='article-card__headline-clamp'></span>
                            </h3>
                            <p class='article-card__excerpt first'></p>
                        </a>
                        <!-- Include video conditionally -->
                        <div class='article-card__meta-bottom'>
                            <span class='article-card__time'>
                                <span class='article-card__time-clamp'></span>
                            </span>
                            <span class='article-card__category'>
                                <a class='article-card__category-link'>
                                    <span data-evt-skip-click='true'></span>
                                </a>
                            </span>
                        </div>
                    </div>
                    ${image(article, this.fallbackImageUrl)}
                </div>
            </article>
            <div class='saved-articles--options'>
            </div>
        `

        const imageLink = element.getElementsByClassName(
            'article-card__image-link'
        )[0] as HTMLAnchorElement
        imageLink.onclick = () => {
            onArticleClick(article)
        }

        const optionsSection = element.getElementsByClassName(
            'saved-articles--options'
        )[0]
        optionsSection.appendChild(deleteButton)
        this.fillArticleCardWithData(element, article)
        return element
    }

    public get pageCount(): number {
        return Math.ceil(this.savedArticles.length / this.perPage)
    }

    private renderPagination() {
        if (this.pageCount === 1) return

        const paginationElement = document.createElement('div')
        const paginationPrevButton = document.createElement('button')
        const paginationPageSummary = document.createElement('div')
        const paginationNextButton = document.createElement('button')

        paginationElement.classList.add('saved-articles--pagination')

        paginationPageSummary.innerText = `${this.currentPage} of ${this.pageCount}`
        if (this.currentPage == 1) {
            paginationPrevButton.style.visibility = 'hidden'
        }
        paginationPrevButton.innerHTML = '&laquo; Previous'
        paginationPrevButton.onclick = this.renderPage.bind(
            this,
            this.currentPage - 1
        )

        if (this.currentPage == this.pageCount) {
            paginationNextButton.style.visibility = 'hidden'
        }
        paginationNextButton.innerHTML = 'Next &raquo;'
        paginationNextButton.onclick = this.renderPage.bind(
            this,
            this.currentPage + 1
        )

        paginationElement.appendChild(paginationPrevButton)
        paginationElement.appendChild(paginationPageSummary)
        paginationElement.appendChild(paginationNextButton)
        this.listContainer?.appendChild(paginationElement)
    }

    private renderPage(page: number) {
        window.scrollTo(0, 0)
        if (page < 1 || page > this.pageCount) {
            return
        }
        this.currentPage = page
        this.renderArticleList()
    }

    private get userType(): 'anonymous' | 'subscriber' | 'register' {
        const isLoggedIn =
            localStorage.getItem(LocalStorageKey.LRTOKENKEY) || ''
        const userData = JSON.parse(
            localStorage.getItem(LocalStorageKey.XID) || '{}'
        ).data

        if (!isLoggedIn) {
            return 'anonymous'
        } else if (isLoggedIn && userData && userData.adLight) {
            return 'subscriber'
        }
        return 'register'
    }

    private async removeArticle(article: SavedArticle) {
        const service = new CustomObjectApiService()
        await service.removeSavedArticle(
            this.token,
            article._id,
            this.objectId,
            this.objectModifiedOn
        )
        await this.loadData()
        this.showMessage(
            'Your changes have been saved',
            this.SAVED_CHANGES_DISMISSED_TIMEOUT
        )
    }

    private showMessage(message: string, dismiss = 0) {
        if (!this.alertMessage || !this.alertContainer) return
        this.alertMessage.innerText = message
        this.alertContainer.classList.remove('hidden-alert')
        this.alertContainer.classList.add('visible')
        // Cancel previous timer, if present
        if (this.alertTimer) {
            window.clearTimeout(this.alertTimer)
        }
        // Start a timer to dismiss
        if (dismiss) {
            this.alertTimer = setTimeout(() => {
                this.closeMessage()
            }, dismiss)
        }
    }

    private closeMessage() {
        if (!this.alertContainer) return
        this.alertContainer.classList.remove('visible')
        this.alertContainer.classList.add('hidden-alert')
    }

    private renderEmptyState() {
        const element = document.createElement('div')
        element.classList.add('saved-articles--empty')
        element.innerHTML = `
            <h3 class='saved-articles--empty--heading'>This is where you’ll find<br />your saved articles</h3>
            <div>${BOOKMARK_ICON}</div>

            <a class='saved-articles--empty--cta' href='/'>
                Find Articles on Homepage
            </a>
        `
        if (this.listContainer) {
            this.listContainer.textContent = ''
            this.listContainer.appendChild(element)
        }
    }
}
